import { ChangeEvent, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Card,
  Checkbox,
  EditIcon,
  Modal,
  PlusIcon,
  Search,
  ShowIcon,
  SliderIcon,
  StatusBadge,
  Tooltip,
  TrashIcon,
  Select,
  SelectOption,
  RadioButton,
  Control,
} from "@forge/common";
import { HubConnectionState } from "@microsoft/signalr";

import ViewCourseCount from "./view-course-count-card/view-course-count-card";
import ViewCourseDetail from "./view-course-detail-card/view-course-detail-card";
import DuplicateCourse from "../common/components/duplicate-course/duplicateCourse";
import { VIEW_COURSE, VIEW_COURSE_LIST, TABLE_HEADING } from "./utils/constants";
import TableListView from "./list-table-view/list-table-view";
import { AppState } from "../../../../common/store/combinedReducer";
import {
  getCourseInstanceViewTemplate,
  getCourseViewTemplate,
  getCardCourseCount,
  deleteCourseTemplate,
  setSelectedCard,
  deleteCourseInstance,
} from "./store/view-course-template.action";
import { useAuth } from "../../../../common/hooks/useAuth";
import {
  setUpSignalRConnection,
} from "../../../../common/utility/Dashboard/utility";
import config from "../../../../common/authentication/config";

import "./view-course-list.scss";
import { formatDate } from "../../../../common/utility/plantDetailUtils";
import { REQUIRED_PERMISSIONS } from "../../../../common/authentication/config";
import {
  btnResponsive,
  getResponsiveFontSize,
  textResponsive,
} from "../common/utils/helper";
import ButtonComponent from "../common/components/button/button";
import PanelContainer from "../common/components/panel-container/panel";
import { showToast } from "../store/model/model.action";

const ViewCourseList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission } = useAuth();
  const courseRef = useRef<HTMLDivElement>(null);
  const [searchValue, _setSearchValue] = useState<string>("");
  const [apiSearchValue, _setApiSearchValue] = useState<string>("");
  const setSearchValue = (val: string) => _setSearchValue(typeof val === 'string' ? val.trim() : val);
  const setApiSearchValue = (val: string) => _setApiSearchValue(typeof val === 'string' ? val.trim() : val);
  const [searchBy, setSearchBy] = useState<number>(0); // 0=All, 1=Course Name, 2=Course Description
  const [sortBy, setSortBy] = useState<string>("");
  const [filterSearchText, setFilterSearchText] = useState<string>("");
  const [filterBy, setFilterBy] = useState<string>("");
  const [showCopyCourseModal, setShowCopyCourseModal] =
    useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showDeleteMultipleModal, setShowDeleteMultipleModal] =
    useState<boolean>(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string>("");
  const [courseToDelete, setCourseToDelete] = useState<any>(null);
  const [viewType, setViewType] = useState<string>("grid");
  const [tableContentData, setTableContentData] = useState<any>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [pageCount, setPageCount] = useState<number>(1);
  const tableSelectAllRef = useRef<(() => void) | null>(null);
  const isApplyingFiltersRef = useRef<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showCourseInstanceDeleteModal, setShowCourseInstanceDeleteModal] =
    useState(false);
  const [
    showCourseInstanceMultipleDeleteModal,
    setShowCourseInstanceMultipleDeleteModal,
  ] = useState(false);
  const hasReachedEnd = useRef<boolean>(false);
  const [searchKey, setSearchKey] = useState<number>(0); // Force search component re-render
  const isSearchingRef = useRef<boolean>(false); // Flag to prevent duplicate search API calls
  const [hasSpecialCharSearch, setHasSpecialCharSearch] = useState<boolean>(false);

  const {
    viewCourseViewData,
    cardCourseCountData,
    viewCourseInstanceData,
    deleteCourseTemplateData,
    selectedCard,
    deleteCourseInstanceData,
  } = useSelector((state: AppState) => {
    return state.viewCourseTemplateReducer;
  });

  const signalRConnection = useSelector(
    (state: AppState) => state.reducer.signalR
  );

  // Helper function to map sortBy dropdown value to sortBy parameter
  const getSortByFromDropdown = (sortByValue: string): number | undefined => {
    switch (sortByValue) {
      case "courseName":
        return 0; // 0 for course name
      case "lastModifiedDate":
        return 1; // 1 for last modified date
      default:
        return undefined; // Don't send sortBy parameter for empty/invalid values
    }
  };

  // Helper function to build API parameters based on current state
  const buildApiParams = useCallback((pageNumber: number = 1, pageSize: number = 15, prevData?: any, excludeSearch: boolean = false, excludeFilterSearch: boolean = false, excludeSort: boolean = false) => {
    const params: any = {
      pageNumber,
      pageSize
    };
    
    if (prevData) {
      params.prevData = prevData;
    }
    
    // Add search parameters based on global search and Search By dropdown selection
    if (!excludeSearch && apiSearchValue && apiSearchValue.trim() !== '') {
      params.searchBy = searchBy; // Use searchBy from dropdown selection (0=All, 1=Course Name, 2=Course Description)
      params.searchValue = apiSearchValue;
    }
    
    // Add filter search parameters if active and not excluded
    if (!excludeFilterSearch && filterSearchText && filterSearchText.trim() !== '' && filterBy) {
      params.filterBy = filterBy;
      params.filterSearchText = filterSearchText;
    }
    
    // Add sort parameters if active and not excluded
    if (!excludeSort && sortBy) {
      const sortByValue = getSortByFromDropdown(sortBy);
      if (sortByValue !== undefined) {
        params.sortBy = sortByValue;
      }
    }
    
    // Add courseStage for course view template (not for course instance)
    if (selectedCard !== 3) {
      params.courseStage = selectedCard === 0
        ? "All"
        : selectedCard === 1
          ? "Published"
          : selectedCard === 2
            ? "Draft"
            : "";
    }
    
    return params;
  }, [apiSearchValue, searchBy, filterSearchText, filterBy, sortBy, selectedCard]);

  // Create refresh callback for SignalR push messages
  const refreshCourseData = useCallback(() => {
    if (selectedCard === 3) {
      dispatch(getCourseInstanceViewTemplate(buildApiParams(1, 15, undefined, true)));
    } else {
      dispatch(getCourseViewTemplate(buildApiParams(1, 15, undefined, true)));
    }
  }, [selectedCard, dispatch, buildApiParams]);

  // Set up SignalR connection only for this specific route
  useEffect(() => {
    if (config.ACADEMY_ORGNAME) {
      // Only establish connection if not already connected
      if (!signalRConnection || signalRConnection.state !== HubConnectionState.Connected) {
        setUpSignalRConnection(config.ACADEMY_ORGNAME, dispatch, refreshCourseData, true);
      }
    }

    // Handle browser tab/window close - this is the only time we want to close the connection
    const handleBeforeUnload = () => {
      if (signalRConnection && signalRConnection.state === HubConnectionState.Connected) {
        signalRConnection.stop();
      }
    };

    // Handle token refresh - re-establish SignalR connection
    const handleTokenRefresh = (event: any) => {
      if (event && (event.key === "token_expiry_time" || event.detail?.key === "token_expiry_time")) {
        // Re-establish connection after token refresh
        if (config.ACADEMY_ORGNAME) {
          setTimeout(() => {
            setUpSignalRConnection(config.ACADEMY_ORGNAME, dispatch, refreshCourseData, true);
          }, 1000); // Small delay to ensure token is properly refreshed
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener("storage", handleTokenRefresh);
    window.addEventListener("custom-storage", handleTokenRefresh);

    // Cleanup function - only remove event listeners, don't close connection on navigation
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener("storage", handleTokenRefresh);
      window.removeEventListener("custom-storage", handleTokenRefresh);
    };
  }, [dispatch, refreshCourseData]); // Include refreshCourseData to get updated callback

  const handleCourseInstanceDeleteClick = (course: any) => {
    setCourseToDelete(course);
    setShowCourseInstanceDeleteModal(true);
  };

  const deleteSingleCourseInstance = () => {
    setShowCourseInstanceDeleteModal(false);
    setIsDeleting(true);
    dispatch(deleteCourseInstance([String(courseToDelete.courseInstanceID)]));
    setCourseToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowCourseInstanceDeleteModal(false);
    setCourseToDelete(null);
  };

  const navigateToCreateCourse = () => {
    navigate("/academy/createCourse");
  };

  // Helper function to build API parameters with explicit searchBy value
  const buildApiParamsWithSearchBy = useCallback((pageNumber: number = 1, pageSize: number = 15, prevData?: any, excludeSearch: boolean = false, excludeFilterSearch: boolean = false, excludeSort: boolean = false, explicitSearchBy?: number) => {
    const params: any = {
      pageNumber,
      pageSize
    };
    
    if (prevData) {
      params.prevData = prevData;
    }
    
    // Add search parameters based on global search and Search By dropdown selection
    if (!excludeSearch && apiSearchValue && apiSearchValue.trim() !== '') {
      params.searchBy = explicitSearchBy !== undefined ? explicitSearchBy : searchBy; // Use explicit value if provided, otherwise use state
      params.searchValue = apiSearchValue;
    }
    
    // Add filter search parameters if active and not excluded
    if (!excludeFilterSearch && filterSearchText && filterSearchText.trim() !== '' && filterBy) {
      params.filterBy = filterBy;
      params.filterSearchText = filterSearchText;
    }
    
    // Add sort parameters if active and not excluded
    if (!excludeSort && sortBy) {
      const sortByValue = getSortByFromDropdown(sortBy);
      if (sortByValue !== undefined) {
        params.sortBy = sortByValue;
      }
    }
    
    // Add courseStage for course view template (not for course instance)
    if (selectedCard !== 3) {
      params.courseStage = selectedCard === 0
        ? "All"
        : selectedCard === 1
          ? "Published"
          : selectedCard === 2
            ? "Draft"
            : "";
    }
    
    return params;
  }, [apiSearchValue, searchBy, filterSearchText, filterBy, sortBy, selectedCard]);

  // // Helper function to map filterBy radio button to searchBy parameter
  // const getSearchByFromFilterBy = (filterByValue: string): number => {
  //   switch (filterByValue) {
  //     case "courseName":
  //       return 1; // Course Name
  //     case "courseDescription":
  //       return 2; // Course Description
  //     default:
  //       return 1; // Default to course name
  //   }
  // };

  // Handle filter operations when sortBy or filter search changes
  useEffect(() => {
    // Skip if we're applying filters via callback to prevent duplicate API calls
    if (isApplyingFiltersRef.current) {
      isApplyingFiltersRef.current = false;
      return;
    }
    
    // Skip if all filter values are empty (typically after tab switch clearing)
    if (!sortBy && !filterSearchText && !filterBy) {
      return;
    }
    
    // Only trigger API call if there's either a dropdown selection or filter search
    if (sortBy || (filterSearchText && filterBy)) {
      setPageCount(1);
      
      if (selectedCard === 3) {
        dispatch(getCourseInstanceViewTemplate(buildApiParams()));
      } else {
        dispatch(getCourseViewTemplate(buildApiParams()));
      }
    }
  }, [sortBy, filterBy, filterSearchText, selectedCard, dispatch]);

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      // Prevent duplicate API calls
      if (isSearchingRef.current) {
        return;
      }
      
      // Prevent API call if search value is empty after trimming
      if (!searchValue) {
        return;
      }

      // Block API call and show no-results in grid if input has special characters
      const trimmedSearch = searchValue.trim();
      if (!/^[A-Za-z0-9 _-]+$/.test(trimmedSearch)) {
        setHasSpecialCharSearch(true);
        return;
      }

      setHasSpecialCharSearch(false);
      
      // Set flag to prevent duplicate calls
      isSearchingRef.current = true;
      
      setApiSearchValue(searchValue);
      setPageCount(1);
      // Use searchBy from dropdown selection (0=All, 1=Course Name, 2=Course Description)
      if (selectedCard === 3) {
        dispatch(
          getCourseInstanceViewTemplate({ 
            ...buildApiParams(),
            searchBy: searchBy,
            searchValue: searchValue
          })
        );
      } else {
        dispatch(
          getCourseViewTemplate({
            ...buildApiParams(),
            searchBy: searchBy,
            searchValue: searchValue
          })
        );
      }
      
      // Reset flag after a short delay
      setTimeout(() => {
        isSearchingRef.current = false;
      }, 500);
    }
  };

  const handleSearchClear = () => {
    // Reset search flag when clearing
    isSearchingRef.current = false;
    setHasSpecialCharSearch(false);
    
    setSearchValue("");
    setApiSearchValue("");
    setPageCount(1);
    // Call API without search parameters when search is cleared
    if (selectedCard === 3) {
      dispatch(getCourseInstanceViewTemplate(buildApiParams(1, 15, undefined, true)));
    } else {
      dispatch(getCourseViewTemplate(buildApiParams(1, 15, undefined, true)));
    }
  };

  const handleApplyFilters = (newSortBy: string) => {
    // Set flag to prevent useEffect from triggering duplicate API calls
    isApplyingFiltersRef.current = true;
    
    // Apply sort changes
    setSortBy(newSortBy);
    setPageCount(1);
    
    // Single API call with sort parameters
    const apiParams = buildApiParams(1, 15, undefined, false, false, false);
    if (newSortBy) {
      const sortByValue = getSortByFromDropdown(newSortBy);
      if (sortByValue !== undefined) {
        apiParams.sortBy = sortByValue;
      }
    }
    
    if (selectedCard === 3) {
      dispatch(getCourseInstanceViewTemplate(apiParams));
    } else {
      dispatch(getCourseViewTemplate(apiParams));
    }
    
    // Reset the flag after a short delay to ensure state updates are complete
    setTimeout(() => {
      isApplyingFiltersRef.current = false;
    }, 100);
  };

  const onItemSelect = (data: string, isChecked: boolean) => {
    if (isChecked) {
      if (!selectedItems.includes(data)) {
        setSelectedItems([...selectedItems, data]);
      }
    } else {
      setSelectedItems(selectedItems.filter((item) => item !== data));
    }
  };

  useEffect(() => {
    if (deleteCourseTemplateData && isDeleting) {
      if (deleteCourseTemplateData.status === 200) {
        dispatch(
          showToast({
            type: "success",
            content:
              deleteCourseTemplateData.message ||
              VIEW_COURSE_LIST.TOAST_MESSAGE.SUCCESS_DELETE_MESSAGE,
          })
        );
        // Clear selected items after successful deletion
        setSelectedItems([]);
        // Refresh data based on selectedCard value
        if (selectedCard !== 3) {
          dispatch(getCourseViewTemplate(buildApiParams()));
        } else {
          dispatch(getCourseInstanceViewTemplate(buildApiParams()));
        }
        // Refresh count cards
        dispatch(getCardCourseCount());
      } else {
        dispatch(
          showToast({
            type: "error",
            content:
              deleteCourseTemplateData.message ||
              VIEW_COURSE_LIST.TOAST_MESSAGE.ERROR_DELETE_MESSAGE,
          })
        );
      }
      setIsDeleting(false);
    }
  }, [deleteCourseTemplateData]);

  useEffect(() => {
    if (deleteCourseInstanceData && isDeleting) {
      if (deleteCourseInstanceData.status === 200) {
        dispatch(
          showToast({
            type: "success",
            content:
              deleteCourseInstanceData.message ||
              VIEW_COURSE_LIST.INSTANCE_TOAST_MESSAGE.SUCCESS_DELETE_MESSAGE,
          })
        );
        // Clear selected items after successful deletion
        setSelectedItems([]);
        // Refresh data based on selectedCard value
        if (selectedCard !== 3) {
          dispatch(getCourseViewTemplate(buildApiParams()));
        } else {
          dispatch(getCourseInstanceViewTemplate(buildApiParams()));
        }
        // Refresh count cards
        dispatch(getCardCourseCount());
      } else {
        dispatch(
          showToast({
            type: "error",
            content:
              deleteCourseInstanceData.message ||
              VIEW_COURSE_LIST.INSTANCE_TOAST_MESSAGE.ERROR_DELETE_MESSAGE,
          })
        );
      }
      setIsDeleting(false);
    }
  }, [deleteCourseInstanceData]);

  useEffect(() => {
    const pageSize = 15;
    setPageCount(1);
    setApiSearchValue(""); // Reset search when switching cards
    setSearchValue("");
    
    // Set flag to prevent filter useEffect from triggering API calls when clearing states
    isApplyingFiltersRef.current = true;
    // Reset search flag when switching cards
    isSearchingRef.current = false;
    setHasSpecialCharSearch(false);
    
    // Clear sort states
    setSortBy("");
    setSearchBy(0); // Reset Search By to "All"
    setSearchKey(prev => prev + 1); // Force search component re-render
    
    if (selectedCard === 3) {
      dispatch(getCourseInstanceViewTemplate(buildApiParams(1, pageSize, undefined, true))); // excludeSearch = true
    } else {
      dispatch(getCourseViewTemplate(buildApiParams(1, pageSize, undefined, true))); // excludeSearch = true
    }
  }, [selectedCard]);

  useEffect(() => {
    dispatch(getCardCourseCount());
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (courseRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = courseRef.current;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

        if (isAtBottom && !hasReachedEnd.current) {
          hasReachedEnd.current = true;
          setPageCount((prevCount) => {
            const pageSize = 15;
            const newCount = prevCount + 1;
            if (selectedCard === 3) {
              let totalCount = cardCourseCountData?.find(
                (c: any) => c.id === 3
              )?.count;
              if (
                totalCount &&
                totalCount !==
                viewCourseInstanceData?.courseInstanceData?.length
              ) {
                dispatch(
                  getCourseInstanceViewTemplate(buildApiParams(newCount, pageSize, viewCourseInstanceData?.courseInstanceData))
                );
              }
            } else {
              let totalCount = cardCourseCountData?.find(
                (c: any) => c.id === selectedCard
              )?.count;
              if (totalCount && totalCount !== viewCourseViewData?.length) {
                dispatch(
                  getCourseViewTemplate(buildApiParams(newCount, pageSize, viewCourseViewData))
                );
              }
            }
            return newCount;
          });
        } else if (!isAtBottom && hasReachedEnd.current) {
          hasReachedEnd.current = false;
        }
      }
    };

    const courseContainer = courseRef.current;
    if (courseContainer) {
      courseContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (courseContainer) {
        courseContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [
    selectedCard,
    cardCourseCountData,
    viewCourseViewData,
    viewCourseInstanceData,
  ]);

  const setCourseInstanceData = () => {
    let initialTableData: any[] = [];
    let filteredCourseData = viewCourseInstanceData?.courseInstanceData || [];
    // Removed local search filtering - now handled by API
    if (filterSearchText && filterBy) {
      let key =
        filterBy.split("course")[0] + "Instance" + filterBy.split("course")[1];
      filteredCourseData = filteredCourseData.filter(
        (course: any) =>
          course[key]?.toLowerCase().includes(filterSearchText.trim().toLowerCase()) ??
          false
      );
    }
    filteredCourseData = handleSort(
      sortBy === "" ? undefined : "asc",
      sortBy,
      filteredCourseData
    );
    filteredCourseData &&
      filteredCourseData.length &&
      filteredCourseData.forEach((course: any, index: number) => {
        if (!course) return;

        let disableRow = course?.inProgress ? course.inProgress : false;
        // Check if the course is expired based on expiryDate
        let effectiveStatus = course?.status;
        if (course?.expiryDate) {
          const expiryDate = new Date(course.expiryDate!);
          if (expiryDate < new Date()) {
            disableRow = true;
            if (effectiveStatus === 1) {
              effectiveStatus = 0; // Override Active to Expired
            }
          }
        }
        // Only ACTIVE (status === 1) and FAILED (status === 3) course instances should be selectable
        if (effectiveStatus !== 1 && effectiveStatus !== 3) {
          disableRow = true;
        }
        // For action buttons: only disable for in-progress and expired, NOT for failed
        let disableActionButtons = course?.inProgress ? course.inProgress : false;
        if (course?.expiryDate) {
          const expiryDate = new Date(course.expiryDate!);
          if (expiryDate < new Date()) {
            disableActionButtons = true;
          }
        }
        // For action buttons: allow ACTIVE (status === 1) AND FAILED (status === 3)
        if (effectiveStatus !== 1 && effectiveStatus !== 3) {
          disableActionButtons = true;
        }
        let opacityStyle = disableRow ? "disable-table-data" : "";
        const courseId =
          String(course?.courseInstanceID) || `courseInstance-${index}`;
        const row = {
          courseid: disableRow ? "disabled-row" : courseId,
          cell: [
            {
              content: (
                <div className="flex">
                  <Checkbox
                    id={courseId}
                    className={`table-checkbox bg-[#202020] rounded-none ${disableRow
                      ? "opacity-30 cursor-disable"
                      : "cursor-pointer"
                      }`}
                    checked={false}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (disableRow) {
                        e.preventDefault();
                        return;
                      }
                      onItemSelect(courseId, e.target.checked);
                    }}
                  />
                </div>
              ),
            },
            {
              content: disableRow ? (
                <div
                  className={`font-normal text-[#45A7EA] ${opacityStyle} w-[100px] xl-low:w-[150px] xl-mid:w-[180px] xl-high:w-[250px] ${textResponsive(
                    "text-sm"
                  )} truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                >
                  {course.courseInstanceName}
                </div>
              ) : (
                <Tooltip
                  content={course.courseInstanceName}
                  position="bottom-left"
                >
                  <div
                    className={`font-normal text-[#45A7EA] ${opacityStyle} w-[100px] xl-low:w-[150px] xl-mid:w-[180px] xl-high:w-[250px] ${textResponsive(
                      "text-sm"
                    )} truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                  >
                    {course.courseInstanceName}
                  </div>
                </Tooltip>
              ),
            },
            {
              content: disableRow ? (
                <div
                  className={`font-normal ${opacityStyle} ${textResponsive(
                    "text-sm"
                  )} w-full w-[100px] xl-low:w-[150px] xl-mid:w-[180px] xl-high:w-[250px] leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                >
                  {course.courseInstanceDescription
                    ? course.courseInstanceDescription
                    : "--"}
                </div>
              ) : course.courseInstanceDescription ? (
                <Tooltip
                  content={
                    course.courseInstanceDescription?.length > 100
                      ? `${course.courseInstanceDescription.substring(
                        0,
                        100
                      )}...`
                      : course.courseInstanceDescription
                  }
                  position="bottom-left"
                >
                  <div
                    className={`font-normal ${opacityStyle} ${textResponsive(
                      "text-sm"
                    )} w-full w-[100px] xl-low:w-[150px] xl-mid:w-[180px] xl-high:w-[250px] leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                  >
                    {course.courseInstanceDescription}
                  </div>
                </Tooltip>
              ) : (
                <div
                  className={`font-normal ${opacityStyle} ${textResponsive(
                    "text-sm"
                  )} w-full w-[100px] xl-low:w-[150px] xl-mid:w-[180px] xl-high:w-[250px] leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                >
                  {course.courseInstanceDescription
                    ? course.courseInstanceDescription
                    : "--"}
                </div>
              ),
            },
            {
              content: (
                <div
                  className={`${textResponsive(
                    "text-sm"
                  )} xl-high:w-[190px] xl-mid:w-[160px] xl-high:mt-2 w-[120px] flex items-center`}
                >
                  {course?.inProgress ? (
                    <div className="border rounded-xl flex justify-center border-1.5 border-[#64C3FF] px-2 mt-1 space-x-2 py-0.5 items-center in-progress-key xl-high:max-w-[200px] opacity-100">
                      <img src="/progress.svg" alt="." />
                      <p className="text-[#64C3FF]">In-Progress</p>
                    </div>
                  ) : effectiveStatus === 2 ? (
                    <div className="border rounded-xl flex justify-center border-1.5 border-[#64C3FF] px-2 mt-1 space-x-2 py-0.5 items-center in-progress-key xl-high:max-w-[200px] opacity-100">
                      <img src="/progress.svg" alt="." />
                      <p className="text-[#64C3FF]">In-progress</p>
                    </div>
                  ) : effectiveStatus === 4 ? (
                    <div className="flex flex-col w-full">
                      <div
                        className={`flex items-center font-normal ml-1 ${opacityStyle}`}
                      >
                        <StatusBadge
                          color="gray"
                          className="mr-2 text-[0.5rem]"
                        />
                        Deleting
                      </div>
                      <div className="border rounded-xl flex justify-center border-1.5 border-[#64C3FF] px-2 mt-2 space-x-2 py-0.5 items-center in-progress-key w-full opacity-100">
                        <img src="/progress.svg" alt="." />
                        <p className="text-[#64C3FF]">In-progress</p>
                      </div>
                    </div>
                  ) : effectiveStatus === 3 ? (
                    <div className="border rounded-xl flex justify-center items-center border-1.5 border-[#FF6464] px-2 mt-1 space-x-2 py-0.5 failed-key xl-high:max-w-[200px] opacity-100">
                      <span className="w-2 h-2 bg-[#FF6464] rounded-full"></span>
                      <p className="text-[#FF6464]">Failed</p>
                    </div>
                  ) : (
                    <div
                      className={`flex items-center font-normal ${opacityStyle}`}
                    >
                      <StatusBadge
                        color={effectiveStatus === 1 ? "green" : (effectiveStatus === 0 || effectiveStatus === 2) ? "gray" : "red"}
                        className="mr-1 text-[0.5rem]"
                      />
                      {effectiveStatus === 1 ? "Active" : effectiveStatus === 0 ? "Expired" : effectiveStatus === 2 ? "Deploying" : "Failed"}
                    </div>
                  )}
                </div>
              ),
            },
            {
              content: (
                <div>
                  {course?.courseTemplateAssociated ? (
                    disableRow ? (
                      <p
                        className={`font-normal ${textResponsive(
                          "text-sm"
                        )} ${opacityStyle} line-height-half w-full leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                      >
                        {course.courseTemplateAssociated}
                      </p>
                    ) : (
                      <Tooltip content={course.courseTemplateAssociated} position="bottom-left">
                        <p
                          className={`font-normal ${textResponsive(
                            "text-sm"
                          )} ${opacityStyle} line-height-half w-full leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                        >
                          {course.courseTemplateAssociated}
                        </p>
                      </Tooltip>
                    )
                  ) : (
                    <div
                      className={`font-normal ${opacityStyle} ${textResponsive(
                        "text-sm"
                      )}`}
                    >
                      --
                    </div>
                  )}
                </div>
              ),
            },
            {
              content: (
                <div
                  className={`font-normal ${opacityStyle} ${textResponsive(
                    "text-sm"
                  )} w-min`}
                >
                  {disableRow ? (
                    <p
                      className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                    >
                      {course?.lastUpdatedBy}
                    </p>
                  ) : (
                    <Tooltip
                      content={course?.lastUpdatedBy}
                      position="bottom-left"
                    >
                      <p
                        className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] xl-high:mb-3 mb-1 truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                      >
                        {course?.lastUpdatedBy}
                      </p>
                    </Tooltip>
                  )}
                  {(() => {
                    if (course.lastUpdatedDate) {
                      return disableRow ? (
                        <p
                          className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                        >
                          {formatDate(course.lastUpdatedDate)}
                        </p>
                      ) : (
                        <Tooltip
                          content={formatDate(course.lastUpdatedDate)}
                          position="bottom-left"
                        >
                          <p
                            className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                          >
                            {formatDate(course.lastUpdatedDate)}
                          </p>
                        </Tooltip>
                      );
                    }
                    return <p>--</p>;
                  })()}
                </div>
              ),
            },
            {
              content: (
                <div
                  className={`flex items-center justify-center w-fit space-x-1 ${disableActionButtons
                    ? "action-button-group-disable disable-table-data"
                    : "action-button-group"
                    }`}
                >
                  {hasPermission(REQUIRED_PERMISSIONS.COURSE_INSTANCE_VIEW) && (
                    <ButtonComponent
                      id={`view-course-${course?.courseInstanceID}`}
                      data-testid={`view-course-${course?.courseInstanceID}`}
                      aria-label="View"
                      tooltip="View"
                      className="border-0 cursor-pointer rounded-none border-none !border-0 shadow-none focus:ring-0 outline-none m-0.5"
                      iconButton={true}
                      disabled={disableActionButtons}
                      onClick={() =>
                        handleViewCourse(
                          `viewDeployedCourseInstance/${course?.courseInstanceID}`
                        )
                      }
                      size="regular"
                      icon={
                        <ShowIcon className={`${btnResponsive("text-sm")}`} />
                      }
                      iconPosition="left"
                      variant="tertiary"
                    />
                  )}
                  {hasPermission(REQUIRED_PERMISSIONS.COURSE_INSTANCE_EDIT) && course?.status !== 3 && (
                    <ButtonComponent
                      id={`Edit-course-${course?.courseInstanceID}`}
                      data-testid={`Edit-courseInstance`}
                      aria-label="Edit"
                      tooltip="Edit"
                      className="border-0 cursor-pointer rounded-none border-none !border-0 shadow-none focus:ring-0 outline-none m-0.5"
                      iconButton={true}
                      disabled={disableActionButtons}
                      onClick={() =>
                        handleEditCourseInstanceNavigate(
                          course?.courseInstanceID
                        )
                      }
                      size="regular"
                      icon={
                        <EditIcon className={`${btnResponsive("text-sm")}`} />
                      }
                      iconPosition="left"
                      variant="tertiary"
                    />
                  )}
                  {hasPermission(
                    REQUIRED_PERMISSIONS.COURSE_INSTANCE_DELETE
                  ) && (
                      <ButtonComponent
                        id={`Delete-course-${course?.courseInstanceID}`}
                        data-testid={`Delete-courseInstance`}
                        aria-label="Delete"
                        tooltip="Delete"
                        disabled={disableActionButtons}
                        className="border-0 cursor-pointer rounded-none border-none !border-0 shadow-none focus:ring-0 outline-none m-0.5"
                        iconButton={true}
                        onClick={() => handleCourseInstanceDeleteClick(course)}
                        size="regular"
                        icon={
                          <TrashIcon className={`${btnResponsive("text-sm")}`} />
                        }
                        iconPosition="left"
                        variant="tertiary"
                      />
                    )}
                </div>
              ),
            },
          ],
        };
        initialTableData.push(row);
      });
    if (initialTableData.length === 0) {
      let noDataMessage: string = "No course instances found";
      noDataMessage = "--";
      setTableContentData([
        {
          courseid: "no-data",
          cell: [
            {
              content: (
                <div className="text-center py-4 text-white">
                  {noDataMessage}
                </div>
              ),
              colSpan: 7, // Span across all columns
            },
          ],
        },
      ]);
    } else {
      setTableContentData(initialTableData);
    }
  };

  const setCourseData = () => {
    let initialTableData: any[] = [];
    let filteredCourseData = viewCourseViewData || [];
    // Removed local search filtering - now handled by API
    if (filterSearchText && filterBy) {
      filteredCourseData = filteredCourseData.filter((course: any) =>
        course[filterBy]?.toLowerCase().includes(filterSearchText.trim().toLowerCase())
      );
    }
    filteredCourseData = handleSort(
      sortBy === "" ? undefined : "asc",
      sortBy,
      filteredCourseData
    );
    filteredCourseData &&
      filteredCourseData.length &&
      filteredCourseData.forEach((course: any, index: number) => {
        let inProgressStatus = [0, 1, 2, 6, 13].includes(course?.courseStage)
          ? true
          : false;
        let failedStatus = [8, 9, 10, 11, 12].includes(course?.courseStage)
          ? true
          : false;
        // Separate variable for opacity styling that includes deleting state
        let shouldApplyOpacity = inProgressStatus || failedStatus || course?.courseStage === 16 || course?.courseStage === 17;
        let opacityStyle = shouldApplyOpacity ? "disable-table-data" : "";
        const courseId = course?.courseId || `course-${index}`;
        const row = {
          courseid:
            inProgressStatus ? "disabled-row" : courseId,
          cell: [
            {
              content: (
                <div className="flex">
                  <Checkbox
                    data-testid={courseId}
                    id={courseId}
                    className={`table-checkbox bg-[#202020] ${inProgressStatus
                      ? "opacity-30 cursor-disable"
                      : "cursor-pointer"
                      }`}
                    checked={false}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (inProgressStatus) {
                        e.preventDefault();
                        return;
                      }
                      onItemSelect(courseId, e.target.checked);
                    }}
                  />
                </div>
              ),
            },
            {
              content:
                inProgressStatus || failedStatus ? (
                  <div
                    className={`font-normal text-[#45A7EA] ${opacityStyle} w-[100px] xl-low:w-[150px] xl-mid:[180px] xl-high:w-[250px] ${textResponsive(
                      "text-sm"
                    )} truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                  >
                    {course?.courseName}
                  </div>
                ) : (
                  <Tooltip content={course?.courseName || ''} position="bottom-left">
                    <div
                      className={`font-normal text-[#45A7EA] ${opacityStyle} w-[100px] xl-low:w-[150px] xl-mid:[180px] xl-high:w-[250px] ${textResponsive(
                        "text-sm"
                      )} truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                    >
                      {course?.courseName}
                    </div>
                  </Tooltip>
                ),
            },
            {
              content:
                inProgressStatus || failedStatus ? (
                  <div
                    className={`font-normal ${opacityStyle} ${textResponsive(
                      "text-sm"
                    )} w-[100px] xl-low:w-[150px] xl-mid:w-[180px] xl-high:w-[250px] leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                  >
                    {course?.courseDescription}
                  </div>
                ) : (
                  <Tooltip
                    content={
                      course?.courseDescription && course.courseDescription.length > 250
                        ? `${course.courseDescription.substring(0, 250)}...`
                        : course?.courseDescription || ''
                    }
                    position="bottom-left"
                  >
                    <div
                      className={`font-normal ${opacityStyle} ${textResponsive(
                        "text-sm"
                      )} w-[100px] xl-low:w-[150px] xl-mid:w-[180px] xl-high:w-[220px] leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                    >
                      {course?.courseDescription
                        ? course?.courseDescription
                        : "--"}
                    </div>
                  </Tooltip>
                ),
            },
            {
              content: (
                <div
                  className={`${textResponsive(
                    "text-sm"
                  )} xl-high:w-[190px] xl-mid:w-[160px] xl-high:mt-2 w-[120px] flex items-center`}
                >
                  {inProgressStatus ? (
                    <div className="border rounded-xl flex justify-center border-1.5 border-[#64C3FF] px-2 mt-2 space-x-2 py-0.5 items-center in-progress-key xl-high:max-w-[200px] opacity-100">
                        <img src="/progress.svg" alt="." />
                        <p className="text-[#64C3FF]">In-Progress</p>
                      </div>
                  ) : course?.courseStage === 16 || course?.courseStage === 17 ? (
                    <div className="flex flex-col w-full">
                      <div className="border rounded-xl flex justify-center border-1.5 border-[#64C3FF] px-2 mt-1 space-x-2 py-0.5 items-center in-progress-key w-full opacity-100">
                        <img src="/progress.svg" alt="." />
                        <p className="text-[#64C3FF]">In-Progress</p>
                      </div>
                      <div
                        className={`flex items-center font-normal ml-1 mt-2 ${opacityStyle}`}
                      >
                        <StatusBadge
                          color="gray"
                          className="mr-2 text-[0.5rem]"
                        />
                        Deleting
                      </div>
                    </div>
                  ) : [7, 8, 9, 10, 11, 12].includes(course?.courseStage) ? (
                    <div className="border rounded-xl flex justify-center items-center border-1.5 border-[#FF6464] px-2 mt-2 space-x-2 py-0.5 failed-key xl-high:max-w-[200px] opacity-100">
                        <span className="w-2 h-2 bg-[#FF6464] rounded-full"></span>
                        <p className="text-[#FF6464]">Failed</p>
                      </div>
                  ) : (
                    <div
                      className={`flex items-center font-normal ${opacityStyle}`}
                    >
                      <StatusBadge
                        color={
                          course?.courseStage === 3 ? "orange" :
                            course?.courseStage === 4 ? "green" :
                              [0, 1, 2, 6, 13].includes(course?.courseStage) ? "gray" :
                                [7, 8, 9, 10, 11, 12].includes(course?.courseStage) ? "red" :
                                  "gray"
                        }
                        className="mr-2 text-[0.5rem]"
                      />
                      {
                        course?.courseStage === 3 ? "Draft" :
                          course?.courseStage === 4 ? "Published" :
                            [0, 1, 2, 6, 13].includes(course?.courseStage) ? "In-progress" :
                              [7, 8, 9, 10, 11, 12].includes(course?.courseStage) ? "Failed" :
                                course?.courseStage === 16 || course?.courseStage === 17 ? "Deleting" : "Unknown"
                      }
                    </div>
                  )}
                </div>
              ),
            },
            {
              content:
                inProgressStatus || failedStatus ? (
                  <p
                    className={`font-normal ${textResponsive(
                      "text-sm"
                    )} ${opacityStyle} line-height-half w-[80px] xl-low:w-[80px] xl-mid:w-[180px] xl-high:w-[220px] leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                  >
                    {course?.vAppTemplateName}
                  </p>
                ) : (
                  <Tooltip
                    content={course?.vAppTemplateName || ''}
                    position="bottom-left"
                  >
                    <p
                      className={`font-normal ${textResponsive(
                        "text-sm"
                      )} ${opacityStyle} line-height-half w-[80px] xl-low:w-[80px] xl-mid:w-[180px] xl-high:w-[220px] leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                    >
                      {course?.vAppTemplateName}
                    </p>
                  </Tooltip>
                ),
            },
            {
              content: (
                <div
                  className={`font-normal ${opacityStyle} ${textResponsive(
                    "text-sm"
                  )} w-min`}
                >
                  {inProgressStatus || failedStatus ? (
                    <p
                      className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                    >
                      {course?.lastModifiedBy}
                    </p>
                  ) : (
                    <Tooltip
                      content={course?.lastModifiedBy || ''}
                      position="bottom-left"
                    >
                      <p
                        className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] xl-high:mb-3 mb-1 truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                      >
                        {course?.lastModifiedBy}
                      </p>
                    </Tooltip>
                  )}
                  {(() => {
                    if (course?.lastModifiedDate) {
                      return inProgressStatus || failedStatus ? (
                        <p
                          className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                        >
                          {formatDate(course.lastModifiedDate)}
                        </p>
                      ) : (
                        <Tooltip
                          content={formatDate(course.lastModifiedDate)}
                          position="bottom-left"
                        >
                          <p
                            className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                          >
                            {formatDate(course.lastModifiedDate)}
                          </p>
                        </Tooltip>
                      );
                    }
                    return "--";
                  })()}
                </div>
              ),
            },
            {
              content: (
                <div
                  className={`flex items-center justify-center w-fit space-x-1 ${shouldApplyOpacity
                    ? "action-button-group-disable disable-table-data"
                    : "action-button-group"
                    }`}
                >
                  {hasPermission(REQUIRED_PERMISSIONS.COURSE_LIBRARY_VIEW) && (
                    <ButtonComponent
                      id={`view-course-${courseId}`}
                      data-testid={`view-course-${courseId}`}
                      aria-label="View"
                      className="border-0 rounded-none border-none !border-0 shadow-none focus:ring-0 outline-none"
                      iconButton={true}
                      disabled={shouldApplyOpacity}
                      onClick={() =>
                        handleViewCourse(`viewCourse/${course?.courseId}`)
                      }
                      size="regular"
                      tooltip="View"
                      icon={
                        <ShowIcon className={`${btnResponsive("text-sm")}`} />
                      }
                      iconPosition="left"
                      variant="tertiary"
                    />
                  )}
                  {[0, 1, 2, 3].includes(Number(course?.courseStage)) && course?.status !== 3 && (
                    <ButtonComponent
                      id={`Edit-course-${courseId}`}
                      data-testid={`Edit-course-${courseId}`}
                      aria-label="Edit"
                      className="border-0 rounded-none border-none !border-0 shadow-none focus:ring-0 outline-none"
                      iconButton={true}
                      disabled={shouldApplyOpacity}
                      onClick={() =>
                        navigate("/academy/editCourse/" + courseId.toString())
                      }
                      size="regular"
                      tooltip="Edit"
                      icon={
                        <EditIcon className={`${btnResponsive("text-sm")}`} />
                      }
                      iconPosition="left"
                      variant="tertiary"
                    />
                  )}
                  {hasPermission(
                    REQUIRED_PERMISSIONS.COURSE_LIBRARY_DELETE
                  ) && (
                      <ButtonComponent
                        id={`Delete-course-${courseId}`}
                        data-testid={`Delete-course-${courseId}`}
                        aria-label="Delete"
                        className="border-0 rounded-none border-none !border-0 shadow-none focus:ring-0 outline-none"
                        iconButton={true}
                        disabled={shouldApplyOpacity}
                        onClick={() => handleDeleteCourse(course)}
                        size="regular"
                        tooltip="Delete"
                        icon={
                          <TrashIcon className={`${btnResponsive("text-sm")}`} />
                        }
                        iconPosition="left"
                        variant="tertiary"
                      />
                    )}
                  {course.courseStage === 4 &&
                    hasPermission(
                      REQUIRED_PERMISSIONS.COURSE_INSTANCE_DEPLOY
                    ) && (
                      <ButtonComponent
                        id={`Deploy-course-${courseId}`}
                        data-testid={`Deploy-course-${courseId}`}
                        aria-label="Deploy"
                        tooltip="Deploy"
                        className="border-0 rounded-none border-none !border-0 shadow-none focus:ring-0 outline-none"
                        iconButton={true}
                        disabled={shouldApplyOpacity}
                        onClick={() => {
                          navigate("/academy/startCourseInstance", {
                            state: { courseId: course.courseId },
                          });
                        }}
                        size="regular"
                        icon={
                          <img
                            src="/play-white.svg"
                            className={`${btnResponsive("text-sm")}`}
                          />
                        }
                        iconPosition="left"
                        variant="tertiary"
                      />
                    )}
                </div>
              ),
            },
          ],
        };
        initialTableData.push(row);
      });
    if (initialTableData.length === 0) {
      setTableContentData([
        {
          courseid: "no-data",
          cell: [
            {
              content: <div className="text-center py-4 text-white">--</div>,
              colSpan: 7, // Span across all columns
            },
          ],
        },
      ]);
    } else {
      setTableContentData(initialTableData);
    }
  };

  useEffect(() => {
    if (selectedCard === 3) {
      setCourseInstanceData();
    } else {
      setCourseData();
    }
  }, [
    viewCourseViewData,
    filterSearchText,
    filterBy,
    selectedCard,
    viewCourseInstanceData?.courseInstanceData,
    sortBy,
  ]);

  // Auto-cleanup selectedItems when courses become inactive
  useEffect(() => {
    if (selectedItems.length === 0) return;

    let validCourseIds: string[] = [];
    
    if (selectedCard === 3) {
      // For course instances
      const courseInstanceData = viewCourseInstanceData?.courseInstanceData || [];
      validCourseIds = courseInstanceData
        .filter((course: any) => {
          if (!course) return false;
          
          // Check if course is expired
          if (course?.expiryDate) {
            const expiryDate = new Date(course.expiryDate);
            if (expiryDate < new Date()) {
              return false; // Expired courses are not selectable
            }
          }
          
          // Only ACTIVE (status === 1) and FAILED (status === 3) course instances should be selectable
          return course?.status === 1 || course?.status === 3;
        })
        .map((course: any, index: number) => 
          String(course?.courseInstanceID) || `courseInstance-${index}`
        );
    } else {
      // For course templates
      const courseData = viewCourseViewData || [];
      validCourseIds = courseData
        .filter((course: any) => {
          if (!course) return false;
          
          // Only remove courses that are in progress (failed courses should remain selectable)
          const inProgressStatus = [0, 1, 2, 6, 13, 16, 17].includes(course.courseStage);
          
          return !inProgressStatus;
        })
        .map((course: any, idx: number) => 
          course?.courseId || course?.CourseID || course?.courseDefintionID || `course-${idx}`
        );
    }
    
    // Filter selectedItems to only include valid course IDs
    const filteredSelectedItems = selectedItems.filter(id => validCourseIds.includes(id));
    
    // Update selectedItems if any invalid items were found
    if (filteredSelectedItems.length !== selectedItems.length) {
      setSelectedItems(filteredSelectedItems);
    }
  }, [viewCourseViewData, viewCourseInstanceData, selectedItems, selectedCard]);

   const handleViewCourse = (courseId: string) => {
    if (courseId) {
      navigate(`/academy/${courseId}`);
    }
  };

  const handleSort = (direction: "asc" | undefined, id: string, data: any) => {
    if (!data || data.length === 0) return data;
    
    // If no sort is selected, return original data
    if (!id || id === "") {
      return data;
    }
    
    // For any sort selection, return data as it comes from API (preserving API order)
    // The API already handles the sorting on the backend
    return data;
  };

  const handleCardChange = (cardId: number) => {
    dispatch(setSelectedCard(cardId));
    setSelectedItems([]);
    setViewType("grid");
    // Reset Search By dropdown to "All" (0) and Sort By dropdown to empty string
    setSearchBy(0);
    setSortBy("");
  };

  const handleDeleteCourse = (course: any) => {
    setCourseToDelete(course);
    setShowDeleteModal(true);
  };

  const handleEditCourseInstanceNavigate = (courseId: number) => {
    navigate("/academy/editCourseInstance/" + courseId.toString());
  };

  const confirmDeleteCourse = () => {
    setShowDeleteModal(false);
    setIsDeleting(true);
    dispatch(deleteCourseTemplate([Number(courseToDelete.courseId)]));
    setCourseToDelete(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setCourseToDelete(null);
  };

  const handleDeleteSelected = () => {
    if (selectedItems.length === 0) {
      return;
    }
    if (selectedCard === 3) {
      setShowCourseInstanceMultipleDeleteModal(true);
    } else {
      setShowDeleteMultipleModal(true);
    }
  };

  const getTotalCount = () => {
    let totalCount = 0;
    let loadedCount = 0;
    
    totalCount = cardCourseCountData?.find(
      (o: any) => o?.id === selectedCard
    )?.count || 0;
    
    if (selectedCard === 3) {
      loadedCount = viewCourseInstanceData?.courseInstanceData?.length || 0;
    } else {
      loadedCount = viewCourseViewData?.length || 0;
    }
    
    return { loadedCount, totalCount };
  };

  const getTitle = () => {
    switch (selectedCard) {
      case 0:
        return VIEW_COURSE_LIST.TITLES.COURSE_ENVIRONMENTS;
      case 1:
        return VIEW_COURSE_LIST.TITLES.TEMPLATES;
      case 2:
        return VIEW_COURSE_LIST.TITLES.DRAFTS;
      case 3:
        return VIEW_COURSE_LIST.TITLES.DEPLOYED_COURSES;
      default:
        return VIEW_COURSE_LIST.TITLES.TEMPLATES;
    }
  };

  const getSelectedCourseNames = () => {
    let selectedCourseName: string[] = [];
    viewCourseViewData &&
      viewCourseViewData.map((course: any) => {
        if (selectedItems.includes(course?.courseId)) {
          selectedCourseName.push(course?.courseName);
        }
      });
    return selectedCourseName;
  };

  const getSelectedCourseTemplateAssociated = () => {
    const selectedTemplateAssociatedSet = new Set<string>();
    if (selectedCard === 3) {
      (viewCourseInstanceData?.courseInstanceData || []).forEach(
        (course: any) => {
          const id = String(course?.courseInstanceID);
          if (
            selectedItems.includes(id) &&
            course?.courseInstanceName &&
            course?.courseTemplateAssociated
          ) {
            selectedTemplateAssociatedSet.add(course.courseTemplateAssociated);
          }
        }
      );
    }
    return Array.from(selectedTemplateAssociatedSet);
  };

  const getSelectedCourseInstanceNames = () => {
    const selectedInstanceNames: string[] = [];
    if (selectedCard === 3) {
      (viewCourseInstanceData?.courseInstanceData || []).forEach(
        (course: any) => {
          const id = String(course?.courseInstanceID);
          if (selectedItems.includes(id)) {
            if (course?.courseInstanceName) {
              selectedInstanceNames.push(course.courseInstanceName);
            }
          }
        }
      );
    }
    return selectedInstanceNames;
  };

  const confirmDeleteMultipleCourses = () => {
    setShowDeleteMultipleModal(false);
    setIsDeleting(true);
    dispatch(deleteCourseTemplate(selectedItems.map((id) => Number(id))));
    // Don't clear selectedItems here - wait for successful deletion
  };

  const deleteMultipleCourseInstance = () => {
    setShowCourseInstanceMultipleDeleteModal(false);
    setIsDeleting(true);
    dispatch(deleteCourseInstance(selectedItems.map((id) => String(id))));
    // Don't clear selectedItems here - wait for successful deletion
  };

  const handleCloseDeleteMultipleModal = () => {
    setShowDeleteMultipleModal(false);
  };
  const handleMultipleDeleteCancel = () => {
    setShowCourseInstanceMultipleDeleteModal(false);
  };

  return (
    <PanelContainer title={VIEW_COURSE.LIBRARY_WELCOME_TEXT}>
      <div
        style={{ height: "calc(100vh - 100px)" }}
        ref={courseRef}
        className="overflow-y-auto"
      >
        <ViewCourseCount
          cardCourseCountData={cardCourseCountData}
          selectedCard={selectedCard}
          setSelectedCard={(cardId: number) => {
            handleCardChange(cardId);
          }}
        />
        <div className="view-course-detail">
          <Card className="!mb-5 !rounded-none !flex !flex-col !h-auto overflow-visible">
            <div className="title-container flex flex-wrap justify-between items-center gap-2 lg:gap-4 p-2 lg:p-4">
              <div
                className={`${getResponsiveFontSize(
                  20
                )} font-medium leading-[34px] text-white`}
              >
                <span>{getTitle()} | </span>
                <span
                  className={`${textResponsive(
                    "text-base"
                  )} font-medium leading-[22px]`}
                >
                  {(() => {
                    const { loadedCount, totalCount } = getTotalCount();
                    return `${loadedCount}/${totalCount} ${VIEW_COURSE_LIST.TOTAL_LABEL}`;
                  })()}
                </span>
              </div>
              {selectedCard !== 3 &&
                hasPermission("dp.twin.course.library.create") && (
                  <ButtonComponent
                    className="xl-high:w-[360px]"
                    data-testid="create-new-course-button"
                    onClick={navigateToCreateCourse}
                    size="regular"
                    icon={
                      <PlusIcon className={`${btnResponsive("text-base")}`} />
                    }
                    iconPosition="right"
                    variant="primary"
                    title={VIEW_COURSE_LIST.CREATE_NEW_COURSE}
                  />
                )}
            </div>
            <div className="search-controls-row flex flex-wrap p-2 lg:p-4 justify-between items-center gap-2 lg:gap-4">
              {/* Left side: Global search and Filter By */}
              <div className="left-controls flex flex-wrap items-baseline gap-2 lg:gap-4 w-full lg:w-auto">
                <Search
                  key={searchKey}
                  className="search-bar search-input-control w-full lg:w-[370px] h-[40px] !border-none !outline-none focus:!border-none focus:!outline-none focus:!ring-0"
                  aria-label="Search"
                  debounce={100}
                  id="course-search"
                  data-testid="course-search"
                  defaultValue=""
                  minCharacters={0}
                  onChange={(val: any) => {
                    const newValue = val?.target?.value;
                    setSearchValue(newValue);
                    // If search is cleared, trigger API call to load default data
                    if (newValue === '') {
                      handleSearchClear();
                    }
                  }}
                  onKeyPress={handleSearchKeyPress}
                  onKeyDown={handleSearchKeyPress}
                  placeholder={VIEW_COURSE_LIST.SEARCH_PLACEHOLDER}
                  size="large"
                />
                {/* Search By Dropdown */}
                <div className="search-select-wrap flex items-center" style={{ height: '40px', marginTop: '2px' }}>
                  <Control className="select-course" style={{ height: '40px', display: 'flex', width: '100%' }}>
                    <Select
                      key={`search-by-${selectedCard}`}
                      id="search-by-select"
                      data-testid="search-by-select"
                      value={searchBy.toString()}
                      placeholder="Search by"
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const newSearchBy = parseInt(event.target.value);
                        // Only update if the parsed value is valid
                        if (!isNaN(newSearchBy) && [0, 1, 2].includes(newSearchBy)) {
                          setSearchBy(newSearchBy);
                          // Only trigger API if there's a global search value
                          if (apiSearchValue && apiSearchValue.trim() !== '') {
                            setPageCount(1);
                            // Use the new searchBy value directly instead of relying on state
                            const apiParams = buildApiParamsWithSearchBy(1, 15, undefined, false, true, false, newSearchBy);
                            if (selectedCard === 3) {
                              dispatch(getCourseInstanceViewTemplate(apiParams));
                            } else {
                              dispatch(getCourseViewTemplate(apiParams));
                            }
                          }
                        }
                      }}
                      className="view-course-select-box responsive-select !border-none !font-normal min-w-[140px] lg:min-w-[160px] h-[40px]"
                      style={{ height: '40px', display: 'flex' }}
                    >
                    <SelectOption value="0" label="All">
                      <div className="flex items-center cursor-pointer px-2 py-1">
                        <RadioButton
                          id="search-by-all"
                          name="search-by"
                          checked={searchBy === 0}
                          onChange={() => {
                            // No need to call setSearchBy here as Select onChange handles it
                            // Only trigger API if there's a global search value
                            if (apiSearchValue && apiSearchValue.trim() !== '') {
                              setPageCount(1);
                              const apiParams = buildApiParamsWithSearchBy(1, 15, undefined, false, true, false, 0);
                              if (selectedCard === 3) {
                                dispatch(getCourseInstanceViewTemplate(apiParams));
                              } else {
                                dispatch(getCourseViewTemplate(apiParams));
                              }
                            }
                          }}
                          value="0"
                        />
                        <span className="ml-2 text-sm font-normal">All</span>
                      </div>
                    </SelectOption>
                    <SelectOption value="1" label={TABLE_HEADING.COURSE_NAME}>
                      <div className="flex items-center cursor-pointer px-2 py-1">
                        <RadioButton
                          id="search-by-courseName"
                          name="search-by"
                          checked={searchBy === 1}
                          onChange={() => {
                            // No need to call setSearchBy here as Select onChange handles it
                            // Only trigger API if there's a global search value
                            if (apiSearchValue && apiSearchValue.trim() !== '') {
                              setPageCount(1);
                              const apiParams = buildApiParamsWithSearchBy(1, 15, undefined, false, true, false, 1);
                              if (selectedCard === 3) {
                                dispatch(getCourseInstanceViewTemplate(apiParams));
                              } else {
                                dispatch(getCourseViewTemplate(apiParams));
                              }
                            }
                          }}
                          value="1"
                        />
                        <span className="ml-2 text-sm font-normal">{TABLE_HEADING.COURSE_NAME}</span>
                      </div>
                    </SelectOption>
                    <SelectOption value="2" label={TABLE_HEADING.COURSE_DESCRIPTION}>
                      <div className="flex items-center cursor-pointer px-2 py-1">
                        <RadioButton
                          id="search-by-courseDescription"
                          name="search-by"
                          checked={searchBy === 2}
                          onChange={() => {
                            // No need to call setSearchBy here as Select onChange handles it
                            // Only trigger API if there's a global search value
                            if (apiSearchValue && apiSearchValue.trim() !== '') {
                              setPageCount(1);
                              const apiParams = buildApiParamsWithSearchBy(1, 15, undefined, false, true, false, 2);
                              if (selectedCard === 3) {
                                dispatch(getCourseInstanceViewTemplate(apiParams));
                              } else {
                                dispatch(getCourseViewTemplate(apiParams));
                              }
                            }
                          }}
                          value="2"
                        />
                        <span className="ml-2 text-sm font-normal">{TABLE_HEADING.COURSE_DESCRIPTION}</span>
                      </div>
                    </SelectOption>
                  </Select>
                </Control>
                </div>
              </div>

              {/* Right side: Sort By, Grid view, Tile view, and Delete selected */}
              <div className="right-controls flex flex-wrap items-center gap-2 lg:gap-4 w-full lg:w-auto">
                {/* Sort By Dropdown */}
                <div className="sort-select-wrap flex items-center" style={{ height: '40px', marginTop: '2px' }}>
                  <Control className="select-course" style={{ height: '40px', display: 'flex', width: '100%' }}>
                    <Select
                      key={`sort-by-${selectedCard}`}
                      id="sort-by-select"
                      data-testid="sort-by-select"
                      value={sortBy}
                      placeholder="Sort by"
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const newSortBy = event.target.value;
                        setSortBy(newSortBy);
                        handleApplyFilters(newSortBy);
                      }}
                        className="view-course-select-box responsive-select !border-none !font-normal min-w-[140px] lg:min-w-[160px] h-[40px]"
                      style={{ height: '40px', display: 'flex' }}
                  >
                    <SelectOption value="" label="Sort by">
                      <div className="flex items-center cursor-pointer px-2 py-1">
                        <RadioButton
                          id="sort-by-none"
                          name="sort-by"
                          checked={sortBy === ""}
                          onChange={() => {
                            setSortBy("");
                            handleApplyFilters("");
                          }}
                          value=""
                        />
                        <span className="ml-2 text-sm font-normal">None</span>
                      </div>
                    </SelectOption>
                    <SelectOption value="courseName" label={TABLE_HEADING.COURSE_NAME}>
                      <div className="flex items-center cursor-pointer px-2 py-1">
                        <RadioButton
                          id="sort-by-courseName"
                          name="sort-by"
                          checked={sortBy === "courseName"}
                          onChange={() => {
                            setSortBy("courseName");
                            handleApplyFilters("courseName");
                          }}
                          value="courseName"
                        />
                        <span className="ml-2 text-sm font-normal">{TABLE_HEADING.COURSE_NAME}</span>
                      </div>
                    </SelectOption>
                    <SelectOption value="lastModifiedDate" label={TABLE_HEADING.LAST_MODIFIED_DATE}>
                      <div className="flex items-center cursor-pointer px-2 py-1">
                        <RadioButton
                          id="sort-by-lastModifiedDate"
                          name="sort-by"
                          checked={sortBy === "lastModifiedDate"}
                          onChange={() => {
                            setSortBy("lastModifiedDate");
                            handleApplyFilters("lastModifiedDate");
                          }}
                          value="lastModifiedDate"
                        />
                        <span className="ml-2 text-sm font-normal">{TABLE_HEADING.LAST_MODIFIED_DATE}</span>
                      </div>
                    </SelectOption>
                  </Select>
                </Control>
                </div>

                <ButtonComponent
                  aria-label="List View"
                  data-testid="list-view-button"
                  className={`text-white filter-button toolbar-icon-button font-normal rounded h-[40px]`}
                  iconButton={true}
                  icon={
                    viewType === "list" ? (
                      <img
                        src="/list-white.svg"
                        alt=""
                        className={`${btnResponsive("icons")}`}
                      />
                    ) : (
                      <img
                        src="/list-gray.svg"
                        alt=""
                        className={`${btnResponsive("icons")}`}
                      />
                    )
                  }
                  onClick={() => {
                    setViewType("list");
                    setSelectedItems([]);
                  }}
                  iconPosition="left"
                  size="regular"
                  variant="tertiary"
                />
                <ButtonComponent
                  aria-label="Grid View"
                  data-testid="grid-view-button"
                  className={`text-white filter-button toolbar-icon-button font-normal rounded h-[40px]`}
                  icon={
                    viewType === "grid" ? (
                      <img
                        src="/grid-white.svg"
                        alt=""
                        className={`${btnResponsive("icons")}`}
                      />
                    ) : (
                      <img
                        src="/grid-gray.svg"
                        alt=""
                        className={`${btnResponsive("icons")}`}
                      />
                    )
                  }
                  iconButton={true}
                  onClick={() => {
                    setViewType("grid");
                    setSelectedItems([]);
                  }}
                  iconPosition="left"
                  size="regular"
                  variant="tertiary"
                />
                <ButtonComponent
                  aria-label="Delete Selected"
                  data-testid="delete-selected-button"
                  className={`text-white filter-button toolbar-action-button font-normal rounded h-[40px]`}
                  onClick={handleDeleteSelected}
                  size="regular"
                  variant="tertiary"
                  title={VIEW_COURSE_LIST.DELETE_SELECTED}
                  disabled={!selectedItems.length}
                />
              </div>
            </div>
            <div className="course-detail-container">
              {(() => {
                let filteredCourseData = viewCourseViewData
                  ? viewCourseViewData
                  : [];
                if (selectedCard === 3) {
                  filteredCourseData =
                    viewCourseInstanceData?.courseInstanceData || [];
                  const isExpired = (expiryDate: string) => {
                    if (expiryDate) {
                      const expiryDateObj = new Date(expiryDate);
                      if (expiryDateObj < new Date()) {
                        return true;
                      }
                    }
                    return false;
                  };
                  filteredCourseData = Array.isArray(filteredCourseData)
                    ? filteredCourseData.map((course: any) => {
                      const expired = isExpired(course?.expiryDate);
                      return {
                        courseId: course?.courseInstanceID
                          ? String(course.courseInstanceID)
                          : "",
                        courseName: course?.courseInstanceName,
                        courseDescription: course?.courseInstanceDescription,
                        vAppTemplateName: course?.courseTemplateAssociated,
                        lastModifiedBy: course?.lastUpdatedBy,
                        lastModifiedDate: course?.lastUpdatedDate,
                        inProgressStatus: expired,
                        expiryData: course?.expiryDate,
                        isEmailSent: course?.isEmailSent,
                        status: expired && course?.status === 1 ? 0 : course?.status,
                      };
                    })
                    : [];
                } else {
                  filteredCourseData = Array.isArray(filteredCourseData)
                    ? filteredCourseData.map((course: any) => ({
                      courseId: course?.courseId || "",
                      courseName: course?.courseName,
                      courseDescription: course?.courseDescription,
                      vAppTemplateName: course?.vAppTemplateName,
                      lastModifiedBy: course?.lastModifiedBy,
                      lastModifiedDate: course?.lastModifiedDate,
                      courseStage: course?.courseStage,
                      inProgressStatus: [
                        0, 1, 2, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17
                      ].includes(course?.courseStage),
                    }))
                    : [];
                }
                // Removed local search filtering - now handled by API
                if (filterSearchText && filterBy) {
                  filteredCourseData = filteredCourseData.filter(
                    (course: any) =>
                      course[filterBy]
                        ?.toLowerCase()
                        .includes(filterSearchText.trim().toLowerCase())
                  );
                }
                filteredCourseData = handleSort(
                  sortBy === "" ? undefined : "asc",
                  sortBy,
                  filteredCourseData
                );
                if (hasSpecialCharSearch) {
                  return (
                    <div className="text-center flex items-center justify-center text-white no-data-found">
                      <p>No courses found</p>
                    </div>
                  );
                }
                if (filteredCourseData.length === 0) {
                  return (
                    <div className="text-center flex items-center justify-center text-white no-data-found">
                      {
                        selectedCard === 2 ? (<p>No draft courses found</p>) :
                          selectedCard === 1 ? (<p>No templates found</p>) :
                            selectedCard === 0 ? (<p>No course environments found</p>) : (<p>No deployed courses found</p>)
                      }
                    </div>
                  );
                } else {
                  return viewType === "grid" ? (
                    <ViewCourseDetail
                      selectedCard={selectedCard}
                      courseData={filteredCourseData}
                      setSelectedItems={setSelectedItems}
                      selectedItems={selectedItems}
                      handleCourseInstanceDeleteClick={handleCourseInstanceDeleteClick}
                    />
                  ) : (
                    <TableListView
                      courseData={tableContentData}
                      selectedCard={selectedCard}
                      setSelectedItems={setSelectedItems}
                      selectedItems={selectedItems}
                      onSelectAllRef={tableSelectAllRef}
                    />
                  );
                }
              })()}
            </div>
            {showCopyCourseModal && (
              <DuplicateCourse
                isOpen={showCopyCourseModal}
                courseId={selectedCourseId}
                onClose={() => {
                  setShowCopyCourseModal(false);
                  setSelectedCourseId("");
                }}
              />
            )}
            {showDeleteModal && (
              <Modal
                backdrop
                onModalClose={handleCloseDeleteModal}
                className="border border-[#E0E0E0] border-[0.2px]"
                title="Delete Course Template"
              >
                <div>
                  <p className="mb-4">
                    {VIEW_COURSE.DELETE.DELETE_COURSE_MESSAGE_LINE1} "
                    {courseToDelete?.courseName}"?{" "}
                  </p>
                  <p>{VIEW_COURSE.DELETE.DELETE_COURSE_MESSAGE_LINE2}</p>
                  <div className="flex gap-3 justify-end pt-4">
                    <Button
                      id="cancel-delete-button"
                      data-testid="cancel-delete-button"
                      variant="secondary"
                      size="regular"
                      onClick={handleCloseDeleteModal}
                    >
                      {VIEW_COURSE.DELETE.CANCEL}
                    </Button>
                    <Button
                      id="confirm-delete-button"
                      data-testid="confirm-delete-button"
                      variant="primary"
                      size="regular"
                      onClick={confirmDeleteCourse}
                    >
                      {VIEW_COURSE.DELETE.DELETE_ANYWAY}
                    </Button>
                  </div>
                </div>
              </Modal>
            )}
            {showDeleteMultipleModal && (
              <Modal
                backdrop
                onModalClose={handleCloseDeleteMultipleModal}
                className="border border-[#E0E0E0] border-[0.2px]"
                title={VIEW_COURSE.MULTIPLE_DELETE.TITLE}
              >
                <div>
                  <p className="mb-4">
                    {
                      VIEW_COURSE.MULTIPLE_DELETE
                        .MULTIPLE_DELETE_COURSE_MESSAGE_LINE1
                    }{" "}
                    {getSelectedCourseNames().length} template(s)?
                  </p>
                  <p className="mb-4">
                    {
                      VIEW_COURSE.MULTIPLE_DELETE
                        .MULTIPLE_DELETE_COURSE_MESSAGE_LINE2
                    }
                  </p>
                  <div className="max-h-48 overflow-y-auto p-3 mb-4">
                    <ul className="space-y-2">
                      {getSelectedCourseNames().map((courseName, index) => (
                        <li
                          key={index}
                          className="text-sm text-gray-300 border-b border-gray-700 pb-1"
                        >
                          {courseName}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex gap-3 justify-end pt-4">
                    <Button
                      id="cancel-multiple-delete-button"
                      data-testid="cancel-multiple-delete-button"
                      variant="secondary"
                      size="regular"
                      onClick={handleCloseDeleteMultipleModal}
                    >
                      {VIEW_COURSE.MULTIPLE_DELETE.CANCEL}
                    </Button>
                    <Button
                      id="confirm-multiple-delete-button"
                      data-testid="confirm-multiple-delete-button"
                      variant="primary"
                      size="regular"
                      onClick={confirmDeleteMultipleCourses}
                    >
                      {VIEW_COURSE.MULTIPLE_DELETE.DELETE_TEMPLATES}
                    </Button>
                  </div>
                </div>
              </Modal>
            )}
            {/* Delete Course Instance Confirmation Modal */}
            {showCourseInstanceDeleteModal && (
              <Modal
                backdrop
                onModalClose={handleDeleteCancel}
                className="border border-[#E0E0E0] border-[0.2px]"
                title="Delete Deployed Course"
              >
                <div>
                  <div className="text-sm text-white-700 mb-1 text-left">
                    {VIEW_COURSE.DELETE.DELETE_INSTANCE_MESSAGE_LINE1} "
                    {courseToDelete?.courseInstanceName}",{" "}
                  </div>
                  <div className="text-sm text-white-700 mb-3 text-left">
                    {VIEW_COURSE.DELETE.DELETE_INSTANCE_MESSAGE_LINE2}
                  </div>
                  <div className="text-sm text-white-700 mb-6 text-left">
                    "{courseToDelete?.courseTemplateAssociated}"
                  </div>
                  <div className="text-sm text-white-700 mb-12 text-left">
                    {VIEW_COURSE.DELETE.DELETE_INSTANCE_MESSAGE_LINE3}
                  </div>
                  <div className="flex justify-center gap-4">
                    <Button
                      id="cancel-instance-delete-button"
                      data-testid="cancel-instance-delete-button"
                      variant="secondary"
                      size="regular"
                      onClick={handleDeleteCancel}
                      className="min-w-[160px] text-sm font-normal"
                    >
                      {VIEW_COURSE.DELETE.CANCEL}
                    </Button>
                    <Button
                      id="confirm-instance-delete-button"
                      data-testid="confirm-instance-delete-button"
                      variant="primary"
                      size="regular"
                      onClick={deleteSingleCourseInstance}
                      className="min-w-[160px] text-sm font-normal"
                    >
                      {VIEW_COURSE.DELETE.FORCE_DELETE}
                    </Button>
                  </div>
                </div>
              </Modal>
            )}

            {/* Delete Course Instance Confirmation Modal */}
            {showCourseInstanceMultipleDeleteModal && (
              <Modal
                backdrop
                onModalClose={handleMultipleDeleteCancel}
                className="border border-[#E0E0E0] border-[0.2px]"
                title="Delete Deployed Courses"
              >
                <div>
                  <div className="text-sm text-white-700 mb-1 text-left">
                    {VIEW_COURSE.DELETE.DELETE_INSTANCE_MESSAGE_LINE1}
                  </div>
                  <div className="max-h-48 overflow-y-auto p-3 mb-4">
                    <ul className="space-y-2">
                      {getSelectedCourseInstanceNames().map(
                        (courseInstanceName, index) => (
                          <li
                            key={index}
                            className="text-sm text-white-700 border-b border-gray-700 pb-1"
                          >
                            {courseInstanceName}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                  <div className="text-sm text-white-700 mb-3 text-left">
                    {VIEW_COURSE.DELETE.DELETE_INSTANCE_MESSAGE_LINE2}
                  </div>
                  <div className="max-h-48 overflow-y-auto p-3 mb-4">
                    <ul className="space-y-2">
                      {getSelectedCourseTemplateAssociated().map(
                        (courseTemplateAssociated, index) => (
                          <li
                            key={index}
                            className="text-sm text-white-700 border-b border-gray-700 pb-1"
                          >
                            {courseTemplateAssociated}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                  <div className="text-sm text-white-700 mb-12 text-left">
                    {VIEW_COURSE.DELETE.DELETE_INSTANCE_MESSAGE_LINE3}
                  </div>
                  <div className="flex justify-center gap-4">
                    <Button
                      id="cancel-multiple-instance-delete-button"
                      data-testid="cancel-multiple-instance-delete-button"
                      variant="secondary"
                      size="regular"
                      onClick={handleMultipleDeleteCancel}
                      className="min-w-[160px] text-sm font-normal"
                      aria-label="Cancel Delete Multiple Instances"
                    >
                      {VIEW_COURSE.DELETE.CANCEL}
                    </Button>
                    <Button
                      id="confirm-multiple-instance-delete-button"
                      data-testid="confirm-multiple-instance-delete-button"
                      variant="primary"
                      size="regular"
                      onClick={deleteMultipleCourseInstance}
                      className="min-w-[160px] text-sm font-normal"
                      aria-label="Confirm Delete Multiple Instances"
                    >
                      {VIEW_COURSE.DELETE.FORCE_DELETE}
                    </Button>
                  </div>
                </div>
              </Modal>
            )}
          </Card>
        </div>
      </div>
    </PanelContainer>
  );
};

export default ViewCourseList;